import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SEO from "../../components/SEO";

interface Play {
  id: number;
  title: string;
  duration: string;
  audience: string;
  writer: string;
  director: string;
  music: string;
  lighting_director: string;
  actors: string;
  image_url: string | null;
  description: string;
}

const PlayShow = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [play, setPlay] = useState<Play | null>(null);
  const [loading, setLoading] = useState(true);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (id) {
      fetch(`/api/plays/${id}?lang=${i18n.language}`)
        .then((res) => res.json())
        .then((data) => {
          setPlay(data);
          setLoading(false);
        })
        .catch((err) => {
          console.error("Play fetch error:", err);
          setLoading(false);
        });
    }
  }, [id, i18n.language]);

  if (loading) {
    return null;
  }

  if (!play) {
    navigate('/play', { replace: true });
    return null;
  }

  return (
    <>
      <SEO
        title={`${play.title} - ${t("pageTitles.plays")}`}
        description={play.description.substring(0, 160)}
        noindex
      />
      {/* Mobile/Tablet Layout */}
      <section className="min-h-[calc(100vh-4rem)] bg-black text-white px-4 py-12 mt-16 lg:hidden">
        <div className="max-w-6xl mx-auto">
          {/* Image */}
          {play.image_url && (
            <div className="w-full mb-8">
              <img
                src={play.image_url}
                alt={play.title}
                className="w-full max-w-md mx-auto rounded-xl shadow-2xl"
              />
            </div>
          )}

          {/* Title */}
          <h2 className="text-3xl mb-6 text-center">{play.title}</h2>

          {/* Information */}
          <div className="text-center mb-8">
            <p className="mb-4 text-lg text-gray-300 flex items-center justify-center gap-4">
              <i className="fa fa-clock text-white" aria-hidden="true"></i>
              {play.duration}
              <i className="fa fa-users text-white ml-6" aria-hidden="true"></i>
              {play.audience}
            </p>

            <div className="space-y-2">
              <p className="mb-2">
                <span className="border-b-2 border-brand-red-text">{t("playShow.writer")}:</span>{" "}
                {play.writer}
              </p>
              <p className="mb-2">
                <span className="border-b-2 border-brand-red-text">{t("playShow.director")}:</span>{" "}
                {play.director}
              </p>
              <p className="mb-2">
                <span className="border-b-2 border-brand-red-text">{t("playShow.music")}:</span>{" "}
                {play.music}
              </p>
              <p className="mb-2">
                <span className="border-b-2 border-brand-red-text">{t("playShow.lightingDirector")}:</span>{" "}
                {play.lighting_director}
              </p>
            </div>

            <div className="mt-6">
              <p className="mb-2">
                <span className="border-b-2 border-brand-red-text">{t("playShow.actors")}:</span>
              </p>
              <p className="text-sm text-gray-300 whitespace-pre-line">
                {play.actors}
              </p>
            </div>
          </div>

          {/* Description */}
          <div className="text-center">
            <h3 className="text-2xl mb-4 border-b-4 border-brand-red-text inline-block">
              {t("playShow.descriptionTitle")}
            </h3>
            <p className="mt-6 text-gray-300 whitespace-pre-line text-lg">
              {play.description}
            </p>
          </div>
        </div>
      </section>

      {/* Desktop Layout */}
      <section className="min-h-[calc(100vh-4rem)] bg-black text-white flex items-center justify-center px-32 py-12 mt-16 hidden lg:flex">
        <div className="flex flex-row gap-24 max-w-6xl w-full items-center">
          {/* Images */}
          {play.image_url && (
            <div className="w-1/2 transform rotate-[-5deg]">
              <img
                src={play.image_url}
                alt={play.title}
                className="w-full max-w-md mx-auto rounded-xl shadow-2xl"
              />
            </div>
          )}

          {/* Information */}
          <div className="w-1/2 text-left -mt-32 ml-8">
            <h2 className="text-4xl mb-4">{play.title}</h2>

            <p className="mb-4 text-lg text-gray-300 flex items-center gap-4">
              <i className="fa fa-clock text-white" aria-hidden="true"></i>
              {play.duration}
              <i className="fa fa-users text-white ml-6" aria-hidden="true"></i>
              {play.audience}
            </p>

            <p className="mb-2">
              <span className="border-b-2 border-brand-red-text">{t("playShow.writer")}:</span>{" "}
              {play.writer}
            </p>
            <p className="mb-2">
              <span className="border-b-2 border-brand-red-text">{t("playShow.director")}:</span>{" "}
              {play.director}
            </p>
            <p className="mb-2">
              <span className="border-b-2 border-brand-red-text">{t("playShow.music")}:</span>{" "}
              {play.music}
            </p>
            <p className="mb-2">
              <span className="border-b-2 border-brand-red-text">{t("playShow.lightingDirector")}:</span>{" "}
              {play.lighting_director}
            </p>

            <p className="mt-6">
              <span className="border-b-2 border-brand-red-text">{t("playShow.actors")}:</span>
            </p>
            <p className="text-sm text-gray-300 whitespace-pre-line mt-1">
              {play.actors}
            </p>
          </div>
        </div>
      </section>

      {/* Desktop Description */}
      <section className="bg-black text-white px-4 lg:px-32 py-12 text-center hidden lg:block">
        <h3 className="text-3xl mb-4 border-b-4 border-brand-red-text inline-block">
          {t("playShow.descriptionTitle")}
        </h3>
        <p className="mt-6 text-gray-300 max-w-4xl mx-auto whitespace-pre-line text-lg">
          {play.description}
        </p>
      </section>
    </>
  );
};

export default PlayShow;